import React from 'react';

// Shared components and utilities
import { 
    OmniIconComponent, 
    OmniDropdownComponent, 
    OmniTooltipNextComponent, 
    OmniStyleComponent,
    OmniRangeSliderComponent,
    OmniCalendarComponent,
    Utils 
} from '../../../../shared';
import { CSVUploadComponent } from './V2/CampaignImport';
import { FiltersMenu } from './V2/CampaignImportExportMenu';

export class FilterCampaign extends React.Component {
    nodeRef = null;
    defaultState = {
        filterOptions: {
            campaignName: '',
            startDate: '',
            endDate: '',
            plannedNetMediaMin: '',
            plannedNetMediaMax: '',
            orderedNetMediaMin: '',
            orderedNetMediaMax: '',
            clientCode: '',
            productCode: '',
            estimateCode: '',
            type: '',
        },
        isMenuActive: false,
        exportStatusOptions: [
            { "id": "Accepted", "value": "Accepted" },
            { "id": "Campaign Sent", "value": "Campaign Sent" },
            { "id": "Error", "value": "Error" },
            { "id": "Export Error", "value": "Export Error" },
            { "id": "Needs Update", "value": "Needs Update" },
            { "id": "Not Exported", "value": "Not Exported" },
            { "id": "Synced", "value": "Synced" },
            { "id": "Sync Error", "value": "Sync Error" },
            { "id": "Updated", "value": "Updated" },
        ],
        displayFilters: true,
        columns: [],
        plannedNetMedia: { min: 0, max: this.props.maxValue },
        orderedNetMedia: { min: 0, max: this.props.maxValue },
        calendarKey: 0,
        plannedNetMediaKey: 0,
        orderedNetMediaKey: 0
    };
    constructor(props) {
        super(props);
        this.state = this.defaultState;
        this.nodeRef = React.createRef();
        this.calendarRef = React.createRef();
    }

    componentDidMount() {
        this.handleDropdownClick();
        this.loadColumns();
        this.hideSignContainers();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.maxValue !== this.props.maxValue) {
            this.setState({
                plannedNetMedia: { min: 0, max: this.props.maxValue },
                orderedNetMedia: { min: 0, max: this.props.maxValue }
            });
        }
        this.hideSignContainers();
    }

    /**
     * Hides sign container elements in the shadow DOM of OmniRangeSliderComponents.
     * Uses requestAnimationFrame to prevent visual flicker during component remount.
     * 
     * @returns {void}
     */
    hideSignContainers = () => {
        requestAnimationFrame(() => {
            const rangeSliders = document.getElementsByTagName("omni-range-slider");
            Array.from(rangeSliders).forEach(slider => {
                const signContainer = slider.shadowRoot?.querySelector("#sign-container");
                if (signContainer) {
                    signContainer.style.setProperty("display", "none", "important");
                }
            });
        });
    }

    /**
     * Loads and processes AG Grid column definitions into a hierarchical tree structure.
     * This method retrieves column definitions from the AG Grid API and transforms them into a 
     * nested tree structure that can be used for column visibility management in the filter panel.
     * It handles both grouped columns (with children) and individual leaf columns, maintaining
     * the hierarchical relationship and visibility state of each column.
     * @description
     * The method performs the following operations:
     * 1. Retrieves column definitions from the AG Grid API
     * 2. Recursively builds a tree structure from flat column definitions
     * 3. Preserves column grouping and hierarchy
     * 4. Tracks visibility state for each column
     * 5. Updates component state with the processed column tree
     */
    loadColumns = () => {
        const { gridApi } = this.props;
        if (!gridApi) return;

        const colDefs = gridApi.getColumnDefs();

        const buildTree = (defs, parentKey = "") =>
            defs.map((def, idx) => {
                const key = `${parentKey}${def.headerName || def.field || def.colId}-${idx}`;
                if (def.children) {
                    return {
                        key,
                        group: true,
                        headerName: def.headerName,
                        children: buildTree(def.children, key + "-")
                    };
                } else {
                    return {
                        key,
                        group: false,
                        colId: def.colId,
                        headerName: def.headerName || def.field,
                        visible: !def.hide
                    };
                }
            });

        this.setState({ columns: buildTree(colDefs || []) });
    };

    /**
     * Toggles the visibility of a specific column in the AG Grid.
     * Searches for the column in both top-level and nested column definitions,
     * then toggles its visibility state and refreshes the column tree.
     * 
     * @param {string} colId - The unique column ID to toggle
     * @returns {void}
     */
    handleToggle = (colId) => {
        const { gridApi } = this.props;
        if (!gridApi) {
            console.error("gridApi is not available");
            return;
        }
        const columnDefs = gridApi.getColumnDefs() || [];
        let colDef = columnDefs.find((c) => c.colId === colId);
        if (!colDef) {
            // search for coldef in children
            const searchInChildren = (children) => {
                for (let child of children) {
                    if (child.colId === colId) return child;
                    if (child.children) {
                        const found = searchInChildren(child.children);
                        if (found) return found;
                    }
                }
                return null;
            };
            colDef = searchInChildren(columnDefs);
            if (!colDef) {
                console.warn(`Column with colId ${colId} not found in any children`);
                return;
            }
        }
        const currentlyVisible = !colDef.hide;
        gridApi.setColumnsVisible([colId], !currentlyVisible);
        this.loadColumns();
    };

    /**
     * Toggles visibility for all columns within a column group.
     * If all children are visible, hides them all; otherwise shows them all.
     * 
     * @param {Object} group - The column group object containing children
     * @returns {void}
     */
    handleGroupToggle = (group) => {
        const { gridApi } = this.props;
        if (!gridApi) return;

        const allChildren = this.getAllLeafCols(group.children);
        const allVisible = allChildren.every((c) => c.visible);

        gridApi.setColumnsVisible(
            allChildren.map((c) => c.colId),
            !allVisible
        );

        this.loadColumns();
    };

    /**
     * Recursively collects all leaf columns from a hierarchical column tree.
     * Traverses through column groups to find individual columns without children.
     * 
     * @param {Array} nodes - Array of column nodes to traverse
     * @returns {Array} Array of leaf column objects
     */
    getAllLeafCols = (nodes) => {
        let result = [];
        nodes.forEach((node) => {
            if (node.group || node.children) {
                result = result.concat(this.getAllLeafCols(node.children));
            } else {
                result.push(node);
            }
        });
        return result;
    };

    /**
     * Toggles visibility for all columns in the grid.
     * If all columns are visible, hides them all; otherwise shows them all.
     * 
     * @returns {void}
     */
    handleViewAllToggle = () => {
        const { gridApi } = this.props;
        if (!gridApi) return;

        const colDefs = gridApi.getColumnDefs() || [];
        const allLeafCols = this.getAllLeafCols(colDefs);

        const allVisible = allLeafCols.every((c) => !c.hide);
        // if all visible → hide all, else → show all
        gridApi.setColumnsVisible(
            allLeafCols.map((c) => c.colId),
            !allVisible
        );

        this.loadColumns();
    };

    /**
     * Recursively renders the hierarchical column tree with interactive checkboxes.
     * Creates column groups with indeterminate states and individual column toggles.
     * 
     * @param {Array} nodes - Array of column nodes to render
     * @param {boolean} isRoot - Whether this is the root level of the tree
     * @returns {Array} Array of JSX elements representing the column tree
     */
    renderTree = (nodes, isRoot = false) =>
        nodes.map((node, index) =>
            node.group ? (
                <div key={node.key} className="column-group">
                    <label>
                        <input
                            type="checkbox"
                            className="toggle-checkbox"
                            ref={(el) => {
                                if (el) {
                                    const allChildren = this.getAllLeafCols(node.children);
                                    const allVisible = allChildren.every((c) => c.visible);
                                    const noneVisible = allChildren.every((c) => !c.visible);

                                    el.checked = allVisible;
                                    el.indeterminate = !allVisible && !noneVisible;
                                }
                            }}
                            onChange={() => this.handleGroupToggle(node)}
                        />
                        {node.headerName}
                    </label>
                    <div className="group-children" style={{ marginLeft: "16px" }}>
                        {this.renderTree(node.children, false)}
                    </div>
                </div>
            ) : (
                <div key={node.key} className={`column-item ${index === 0 && isRoot ? 'first-column-item' : ''}`}>
                    <label>
                        <input
                            type="checkbox"
                            className="toggle-checkbox"
                            checked={node.visible}
                            onChange={() => this.handleToggle(node.colId)}
                        />
                        {node.headerName}
                    </label>
                </div>
            )
        );

    handleDropdownClick() {
        if (!this.loaded) {
            this.loaded = true;
            // Get all dropdowns on the page that aren't hoverable.
            this.dropdowns = document.querySelectorAll('.dropdown:not(.is-hoverable)');

            if (this.dropdowns.length > 0) {
                // For each dropdown, add event handler to open on click.
                this.dropdowns.forEach((el) => {
                    el.addEventListener('click', (e) => {
                        //first close all dropdown
                        this.closeDropdowns(e);
                        if (!el.classList.contains("is-active")) {
                            el.classList.toggle('is-active');
                            let container = document.querySelectorAll('.dropdown-content');
                            let exists = false;
                            container.forEach((item) => {
                                if ((!exists && item === e.target) || item.contains(e.target)) {
                                    exists = true
                                }
                            })

                            if (!exists) {
                                e.stopPropagation();
                                e.preventDefault();
                            }
                        }
                    });
                });

                // If user clicks outside dropdown, close it.
                document.addEventListener('click', (e) => {
                    this.closeDropdowns(e);
                });
            }
        }
    }

    closeDropdowns = (e) => {
        this.dropdowns.forEach(function (el) {
            el.classList.remove('is-active');
        });
    }

    handleModalClick = (event) => {
        event.stopPropagation();
    }

    /**
    * The above code is a JavaScript function called `toggleMenu` that is used to toggle the
    * visibility of a dropdown menu. When the function is called, it prevents the default behavior of
    * the event and stops it from propagating further. It then toggles the visibility of the dropdown
    * menu by adding or removing the CSS class "show" to the element with the ID
    * "dropdown-menu-content".
    * @param {html event} event
    */
    toggleMenu = (event) => {
        event.stopPropagation();
        event.preventDefault();
        let filterMenu = document.getElementById(this.props.id);
        let filterIcon = event.currentTarget || document.getElementById('filter-icon'+this.props.id);
        filterMenu.classList.toggle("show");
        
        const isMenuActive = filterMenu?.classList.contains("show") ? true : false;
        
        if (isMenuActive && filterIcon) {
            const iconRect = filterIcon.getBoundingClientRect();
            const menuRect = filterMenu.getBoundingClientRect();
            const menuParent = filterMenu.parentElement;
            const screenWidth = window.innerWidth;
            const screenMidpoint = screenWidth / 2;
            
            if (menuParent) {
                // Position the filter panel relative to the filter icon
                menuParent.style.position = 'fixed';
                menuParent.style.top = `${iconRect.top - 80}px`;
                menuParent.style.zIndex = '1000';
                
                // Check if filter icon is on left or right side of screen
                if (iconRect.left < screenMidpoint) {
                    // Icon is on left side - position menu on left side
                    menuParent.style.right = 'auto';
                    menuParent.style.left = `${iconRect.y}px`;
                } else {
                    // Icon is on right side - position menu on right side
                    menuParent.style.right = `${screenWidth - iconRect.right + (menuRect.width + iconRect.width)}px`;
                    menuParent.style.left = 'auto';
                }
            }
            
            document.querySelectorAll("om2-dropdown-impl").forEach(eachEle => {
                eachEle.shadowRoot.querySelector(".dropdown-content").style.position = "initial";
            });
        }
        
        this.setState({ isMenuActive })
    }

    getEventTarget(e) {
        e = e || window.event;
        return e.target || e.srcElement;
    }

    onValueChange = (type, event) => {
        let target = this.getEventTarget(event);
        const value = target.textContent;
        this.updateFilterOptions(type, value);
    };

    /**
    *  Description The above code is defining a function called `onDataChange` that takes two parameters: `type`
    *  and `event`. Inside the function, it checks if the `type` parameter is either "startDate" or
    *  "endDate". If it is, it formats the `value` of the `event.target` using a utility function
    *  called `formatDateObject` from a module called `Utils`. Finally, it calls another function
    *  called `updateFilterOptions` with the `type` and `value` parameters.
    *  @param {string} type
    *  @param {html event} event
    */
    onDataChange = (type, event) => {
        let value = Utils.getValue(event);
        if (type === "date") {
            let dates = value.split(",");
            if(this.props?.id ==='unmap-campaign'){
                value = {
                    startDate:dates[0],endDate:dates[1]
                };
            }else{
                const startDate = dates[0] ? Utils.formatDateObject(dates[0]) : "";
                const endDate = dates[1] ? Utils.formatDateObject(dates[1]) : "";
                value = {
                    startDate,endDate
                };
            }
        }
        this.updateFilterOptions(type, value);
    }

    updateFilterOptions = (type, value) => {
        let filterOptions = { ...this.state.filterOptions };
        if(type === 'orderedNetMedia' || type === 'plannedNetMedia'){
            filterOptions[type + 'Min'] = value.min;
            filterOptions[type + 'Max'] = value.max;
        } else if(type === 'date'){
            filterOptions['startDate'] = value.startDate;
            filterOptions['endDate'] = value.endDate;
        } else {
            filterOptions[type] = value;
        }
        this.setState({ filterOptions: filterOptions });
    }

    /**
    * Description The above code is defining an arrow function called `onClearFilter` that takes an event object
    * as its parameter. Inside the function, it sets the `filterOptions` state of the component to its
    * default state, calls the `onApplyFilter` function with the default filter options as its
    *  argument, and then toggles the menu. This function is likely used to reset the filter options to
    *  their default values and apply the default filter to the data being displayed. 
    *  @param {html event} event
    */
    onClearFilter = (event) => {
       let plannedNetMedia = { min: 0, max: this.props.maxValue };
       let orderedNetMedia = { min: 0, max: this.props.maxValue };
        this.setState({ 
            filterOptions: this.defaultState.filterOptions,
            calendarKey: this.state.calendarKey + 1,
            plannedNetMediaKey: 0,
            orderedNetMediaKey: 0,
            orderedNetMedia,plannedNetMedia
        },()=>{
            this.props.onApplyFilter(this.defaultState.filterOptions,true);
            this.toggleMenu(event);
            setTimeout(() => {
                this.toggleMenu(event);
            })
        });
    }

    /**
    * Description The above code is defining an arrow function called `applyFilter` that takes an event as an
    * argument. Within the function, it calls a function `onApplyFilter` that is passed as a prop to
    * the component, and passes in the current state of `filterOptions` as an argument. It then calls
    * another function `toggleMenu` and passes in the event as an argument. The purpose of this code
    * is to apply a filter based on the current state of `filterOptions` and then toggle a menu.
    * @param {html event} event
    */
    applyFilter = (event) => {
        this.props.onApplyFilter(this.state.filterOptions);
        this.toggleMenu(event);
    }


    // Method to find out passed item is null, mostly used as callback method in array methods
    isNull = (item) => !item

    changeTab = (event) => {
        const tab = event.target.innerText;
        if (tab === "Filters") {
            this.setState({ displayFilters: true });
        } else if (tab === "Columns") {
            this.setState({ displayFilters: false });
        }
    }

    handleExportMenuClose = (event) => {
        event.target.closest("omni-menu")?.close();
    };

    handleImportMenuItemClick = (event) => {
        const modalEl = document.getElementById("csv-import-modal");
        if (modalEl) {
          modalEl.show();
        }
        this.handleExportMenuClose(event);
    };

    closeCSVDialog = () => {
        const modalEl = document.getElementById("csv-import-modal");
        if (modalEl) {
          modalEl.close();
        }
    };
    
    handleExportMenuItemClick = (event) => {
        this.handleExportMenuClose(event);
    };

    /**
     * Updates range slider values from manual input with boundary validation.
     * Ensures min values don't exceed max and vice versa, then forces component re-render.
     * 
     * @param {string} type - Input type ('plannedNetMediaMin', 'plannedNetMediaMax', 'orderedNetMediaMin', 'orderedNetMediaMax')
     * @param {Event} event - Input change event
     * @returns {void}
     */
    setRangeValue = (type, event) => {
        let value = Number(event.target.value);
        let filterOptions = { ...this.state.filterOptions };
        if(type === 'plannedNetMediaMin' || type === 'plannedNetMediaMax'){
            // Enforce min/max constraints
            if(type === 'plannedNetMediaMin') {
                const maxLimit = this.state?.plannedNetMediaMax || this.state?.plannedNetMedia?.max;
                value = Math.max(0, Math.min(value, maxLimit));
            } else {
                const minLimit = this.state?.plannedNetMediaMin || this.state?.plannedNetMedia?.min;
                value = Math.max(minLimit, Math.min(value, this.props.maxValue));
            }

            filterOptions[type] = value;
            this.setState((prevState) => ({
                plannedNetMedia: {
                    ...prevState.plannedNetMedia,
                    [type === 'plannedNetMediaMin' ? 'min' : 'max']: value
                },
                filterOptions,
                plannedNetMediaKey: prevState.plannedNetMediaKey + 1
            }));
        } else if(type === 'orderedNetMediaMin' || type === 'orderedNetMediaMax'){
            filterOptions[type] = value;
            this.setState((prevState) => ({
                orderedNetMedia: {
                    ...prevState.orderedNetMedia,
                    [type === 'orderedNetMediaMin' ? 'min' : 'max']: value
                },
                filterOptions,
                orderedNetMediaKey: prevState.orderedNetMediaKey + 1
            }));
        }
    }
        


    render() {
        const { isMenuActive, filterOptions, columns } = this.state;
        const disableApplyFilter = (Object.values(filterOptions).every(this.isNull));  // If every value is null, disable the apply button
        const allLeafCols = this.getAllLeafCols(columns);
        const allVisible = allLeafCols.every((c) => c.visible);
        const noneVisible = allLeafCols.every((c) => !c.visible);

        return (
            <div>
                <OmniStyleComponent>
                    {this.props.isTabRequired? 
                    (<>
                    <div
                        className={`menu-trigger ag-grid-filter-wrapper filter-container ${isMenuActive ? "bg-blue" : ""}`}
                    >
                        <OmniTooltipNextComponent
                            className="aggrid-filter-menu-trigger"
                            onClick={this.toggleMenu}
                            id={'filter-icon'+this.props.id}
                        >
                            <button slot="invoker" aria-haspopup="true" className={`icon`}>
                                <OmniIconComponent slot="end" className="menu-trigger is-size-2" icon-id="omni:interactive:filter" />
                            </button>
                            <div slot="content">Filters</div>
                            <span>All Filters</span>
                        </OmniTooltipNextComponent>
                        <FiltersMenu
                            handleExportMenuItemClick={this.handleExportMenuItemClick}
                            handleImportMenuItemClick={this.handleImportMenuItemClick} />
                    </div>
                    <CSVUploadComponent
                            closeCSVDialog={this.closeCSVDialog} />
                    </>
                    )
                    :
                    <>
                    <OmniTooltipNextComponent
                            className="aggrid-filter-menu-trigger"
                            onClick={this.toggleMenu}
                            id={'filter-icon'+this.props.id}
                        >
                                <OmniIconComponent className="is-size-1 subheader-icon" icon-id="omni:interactive:funnel"></OmniIconComponent> 
                            <div slot="content">Filters</div>
                        </OmniTooltipNextComponent>
                    </>
                    }

                    {/* <Draggable nodeRef={this.nodeRef} axis="both" bounds='body' defaultPosition={{ x: 54, y: 8 }}> */}

                        <div ref={this.nodeRef} className={'aggrid-filter-panel'}>
                            <div className="dropdown-menu-content landing-page-filters aggrid-filter-container" id={this.props.id} role="menu">
                                <div>

                                    <div className="filter-header">
                                       {
                                        this.props.isTabRequired? 
                                        <div>
                                            <button className={'button' + (this.state.displayFilters ? "" : " is-text")} onClick={event => this.changeTab(event)}>Filters</button>
                                            <button className={'button' + (this.state.displayFilters ? " is-text" : "")} onClick={event => this.changeTab(event)}>Columns</button>
                                        </div>:
                                            <h3 className='filter-title'>Filters</h3>
                                            
                                       } 
                                        <div className='filter-close-icon'>
                                            <OmniIconComponent onClick={this.toggleMenu} className="is-size-1" icon-id="omni:interactive:close"></OmniIconComponent>
                                        </div>
                                    </div>

                                    <hr className='filter-header-bar' />
                                    {this.state.displayFilters ?
                                    <>
                                        <div className="filter-sections">
                                            <div className='filter-sections-list'>
                                                <section className="section-item media-direction-column">
                                                    <label className="media-flex media-justify-space-between is-7 filter-label">
                                                        <span className="filter-label-text">Campaign name</span>
                                                    </label>
                                                    <div className="full-width">
                                                        <input type="text" placeholder="Campaign Name" value={this.state.filterOptions.campaignName}
                                                            onChange={this.onDataChange.bind(this, 'campaignName')} className="input text filter-field">
                                                        </input>
                                                    </div>
                                                </section>
                                                <hr/>
                                                <section className="section-item media-direction-column">
                                                    <label className="media-flex media-justify-space-between is-7 filter-label">
                                                        <span className="filter-label-text">Export Status</span>
                                                    </label>
                                                    <div className="full-width">
                                                        <OmniDropdownComponent
                                                            typeahead
                                                            searchindropdown
                                                            options={this.state.exportStatusOptions}
                                                            value={this.state.filterOptions.status ? [this.state.filterOptions.status] : []}
                                                            onDropdownChange={(event) => this.onDataChange("status", event)}
                                                            placeholder="Select Option"
                                                            hidefooter={true}
                                                        ></OmniDropdownComponent>
                                                    </div>
                                                </section>
                                                <hr/>
                                                <section className="section-item media-direction-column side-section">
                                                    <label className="media-flex media-justify-space-between is-7 filter-label">
                                                        <span className="filter-label-text">Select a range:</span>
                                                    </label>
                                                    <div className="full-width">
                                                        <OmniCalendarComponent
                                                            key={this.state.calendarKey}
                                                            range
                                                            onCalendarChange={(e) => this.onDataChange('date', e)}
                                                            style={{width: "fit-content"}}
                                                            name={this.props.id}
                                                            id={this.props.id + "-calendar"}>
                                                        </OmniCalendarComponent>
                                                    </div>
                                                </section>
                                                <hr/>
                                                <section className="section-item media-direction-column side-section">
                                                    {/* <div className="media-flex media-justify-space-between is-7 filter-label">
                                                        <span className="filter-label-text">Campaign name</span>
                                                    </div> */}
                                                    <label className="media-flex media-justify-space-between is-7 filter-label">
                                                        <span className="filter-label-text">Planned Net Media Min</span>
                                                    </label>
                                                    <div className="full-width">
                                                        <OmniRangeSliderComponent key={this.state.plannedNetMediaKey} min={0} max={this.props.maxValue} value={this.state.plannedNetMedia} step={100} onRangeSliderChange={(event) => this.onDataChange('plannedNetMedia', event)}></OmniRangeSliderComponent>
                                                        <div className='range-inputs'>
                                                            <input type="number" min={0} max={this.state?.plannedNetMediaMax || this.state?.plannedNetMedia?.max} value={this.state?.plannedNetMediaMin || this.state?.plannedNetMedia?.min} onChange={this.setRangeValue.bind(this, 'plannedNetMediaMin')}></input>
                                                            <input type="number" min={this.state?.plannedNetMediaMin || this.state?.plannedNetMedia?.min} max={this.props.maxValue} value={this.state?.plannedNetMediaMax || this.state?.plannedNetMedia?.max} onChange={this.setRangeValue.bind(this, 'plannedNetMediaMax')}></input>
                                                        </div>
                                                    </div>
                                                </section>
                                                <hr/>
                                                {!this.props?.isOldcampaignTable && <section className="section-item media-direction-column side-section">
                                                    <label className="media-flex media-justify-space-between is-7 filter-label">
                                                        <span className="filter-label-text">Ordered Net Media Min</span>
                                                    </label>
                                                    <div className="full-width">
                                                        <OmniRangeSliderComponent key={this.state.orderedNetMediaKey} min={0} max={this.props.maxValue} value={this.state.orderedNetMedia} step={100} onRangeSliderChange={(event) => this.onDataChange('orderedNetMediaMin', event)}></OmniRangeSliderComponent>
                                                        <div className='range-inputs'>
                                                            <input type="number" min={0} max={this.state?.orderedNetMediaMax || this.state?.orderedNetMedia?.max} value={this.state?.orderedNetMediaMin || this.state?.orderedNetMedia?.min} onChange={this.setRangeValue.bind(this, 'orderedNetMediaMin')}></input>
                                                            <input type="number" min={this.state?.orderedNetMediaMin || this.state?.orderedNetMedia?.min} max={this.props.maxValue} value={this.state?.orderedNetMediaMax || this.state?.orderedNetMedia?.max} onChange={this.setRangeValue.bind(this, 'orderedNetMediaMax')}></input>
                                                        </div>
                                                    </div>
                                                </section>}
                                                <hr/>

                                                <section className="section-item media-direction-column">
                                                    <label className="media-flex media-justify-space-between is-7 filter-label">
                                                        <span className="filter-label-text">Client Code</span>
                                                    </label>
                                                    <div className="full-width">
                                                        <input
                                                            type="text"
                                                            placeholder="Enter Client Code"
                                                            value={filterOptions.clientCode}
                                                            onChange={this.onDataChange.bind(this, 'clientCode')}
                                                            className="input text filter-field" />
                                                    </div>
                                                </section>
                                                <hr/>
                                                <section className="section-item media-direction-column">
                                                    <label className="media-flex media-justify-space-between is-7 filter-label">
                                                        <span className="filter-label-text">Product Code</span>
                                                    </label>
                                                    <div className="full-width">
                                                        <input
                                                            type="text"
                                                            placeholder="Enter Product Code"
                                                            value={filterOptions.productCode}
                                                            onChange={this.onDataChange.bind(this, 'productCode')}
                                                            className="input text filter-field" />
                                                    </div>
                                                </section>
                                                <hr/>
                                                <section className="section-item media-direction-column">
                                                    <label className="media-flex media-justify-space-between is-7 filter-label">
                                                        <span className="filter-label-text">Estimate Code</span>
                                                    </label>
                                                    <div className="full-width">
                                                        <input
                                                            type="text"
                                                            placeholder="Enter Estimate Code"
                                                            value={filterOptions.estimateCode}
                                                            onChange={this.onDataChange.bind(this, 'estimateCode')}
                                                            className="input text filter-field" />
                                                    </div>
                                                </section>
                                                <hr/>
                                            </div>
                                        </div>
                                        <div className="landing-page-filter-action-containor">
                                            <hr/>
                                            <div className="action-btns">
                                                <a className="clear-all-link" onClick={this.onClearFilter}>Clear All</a>
                                                <button disabled={disableApplyFilter} onClick={this.applyFilter}>Apply filters</button>
                                            </div>
                                        </div>
                                    </>
                                    :
                                        <div className="filter-sections">
                                            <div className="view-all">
                                                <label>
                                                    <input type="checkbox" className="toggle-checkbox" onChange={this.handleViewAllToggle}
                                                        ref={(el) => {
                                                            if (el) {
                                                                el.checked = allVisible;
                                                                el.indeterminate = !allVisible && !noneVisible;
                                                            }
                                                        }} />
                                                    View All Columns
                                                </label>
                                            </div>
                                            <hr className="columns-separator" />
                                            {this.renderTree(columns, true)}
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    {/* </Draggable> */}
                </OmniStyleComponent>
            </div>
        )
    }
}
